import { useRef } from "react";
import { MoreHorizontal, Pencil } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/modern/components/ui/dropdown-menu";
import { cn } from "@/modern/utils";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { ChecklistMenuItems } from "@/react/components/entityViews/checklist/components/ChecklistMenuItems";
import type { CheckGridOverviewItem } from "./types";

export type CheckgridMenuAction = "edit" | "openEntity" | "archive" | "copyLink" | "duplicate" | "import" | "export" | "nudge" | "delete";

type Props = {
  grid: CheckGridOverviewItem;
  onAction: (action: CheckgridMenuAction, grid: CheckGridOverviewItem, extras?: { file?: File }) => void;
  size?: "sm" | "md";
};

export function CheckgridMenu({ grid, onAction, size = "sm" }: Props) {
  const { user } = useSwivuBryntumContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canEdit = Boolean(user?.can(grid.can_edit_grant ?? GRANTS.grant_four));

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onAction("import", grid, { file });
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <>
      <input ref={fileInputRef} type="file" accept=".txt" className="hidden" onChange={handleFileChange} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn("rounded-md text-muted-foreground hover:bg-muted hover:text-foreground", size === "sm" ? "size-7" : "size-8")}
            aria-label={`Manage ${grid.name}`}
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className="size-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem onClick={() => onAction("edit", grid)} disabled={!canEdit}>
            <Pencil className="size-4" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <ChecklistMenuItems
            archived={grid.archived}
            canEdit={canEdit}
            onArchive={() => onAction("archive", grid)}
            onCopyLink={() => onAction("copyLink", grid)}
            onDuplicate={() => onAction("duplicate", grid)}
            onImport={() => fileInputRef.current?.click()}
            onExport={() => onAction("export", grid)}
            onNudge={() => onAction("nudge", grid)}
            onDelete={() => onAction("delete", grid)}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
